<script setup lang="ts">
// TODO refactor that shit
import SectionComponent from '@/vue/molecules/components/section-component.vue';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {
  computed,
  ref,
} from 'vue';
import type {
  Component,
  Intervention,
} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import LoadingError from '@/vue/templates/loading-error.vue';
import {useLoadingDataRefNull} from '@/ts/composables/pure/use-loading-data-ref';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import type {SessionRequest} from '@/ts/types/dto/session-request-dto';
import {useLogger} from '@/ts/composables/pure/use-logger';
import type {ComponentPublicInstance} from '@vue/runtime-core';
import {useScrollToElement} from '@/ts/composables/pure/use-scroll-to';
import {useSectionRefs} from '@/ts/composables/pure/use-section-refs';
import {SMALL_PADDING} from '@/ts/utils/css-consts';
import {getId} from '@/ts/composables/pure/use-get-id';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {useSnackbarStore} from '@/ts/store/snackbar-store';
import {useI18n} from 'vue-i18n';

const props = defineProps<{
  homeworkSessionId?: UuidDTO;
}>();

const isOpen = defineModel<boolean>();

const api = useApi();
const snackbarStore = useSnackbarStore();
const {t} = useI18n();

const {sid: routeSid, iid} = useRouterSectionParams();
const sid = computed(() => props.homeworkSessionId ?? routeSid.value);
const hideContinue = ref(false);

const model = ref<SessionRequest[][]>([]);
const intervention = useInterventionStore();

const currentIndex = ref(1);  // image no text should come with 2nd component
const sectionRefs = useSectionRefs();

const {session} = useSessionId(sid);

if (session.value!.homework[1]) {
  throw new AppError('Multiple homeworks are not supported', ErrorCodesDto.UNKNOWN);
}

const isCompleted = computed(() => Boolean(session.value!.homework[0].completedDate));

const components = computed<Component[]>(() => {
  return session.value!.homework[0].pages.flatMap(homework => homework.components);
});

const allContentRead = computed(() => {
  return currentIndex.value >= components.value.length;
});

const [_, loading, error] = useLoadingDataRefNull(async() => {
  if (isCompleted.value) {
    // Show all components for completed homework (read-only)
    currentIndex.value = components.value.length;
    return;
  }
  if (!session.value!.homework[0].userHomeworkId) {
    const homework = await api.me.postHomeWork(
      iid.value,
      sid.value,
    );
    intervention.value!.sessions.find(ses => ses.id === sid.value)!.homework = [homework];
  }
});
const isSubmitLabel = computed(() => currentIndex.value >= components.value.length - 1);

 
const scrollToSection = useScrollToElement(SMALL_PADDING);
const logger = useLogger();
const [saveSession, savingInProgress] = useLoadingNotification(
    async(): Promise<Intervention> => {
      const currentModel: SessionRequest[] | null = model.value[currentIndex.value] ?? null;
      logger.info(`Increasing currentIndex ${currentIndex.value}`)();
      currentIndex.value++;
      // Use the same guard wording as session-cms-content.vue so the two
      // organisms can't silently desync if one is edited (IPD-2759 review
      // chemarubio). `currentIndex < length` and `!allContentRead` were
      // equivalent here.
      if (currentIndex.value < components.value.length) {
        const revealedIndex = currentIndex.value;
        scrollToSection(() => sectionRefs.resolve(revealedIndex));
      }
      // we have temporary answers or we are at the last component
      if ((currentModel?.length ?? 0) > 0 || allContentRead.value) {
        const answers: SessionRequest[] = model.value
          .flatMap((val: SessionRequest[]) => val)
          .filter((el: SessionRequest | null) => el);
        logger.info('Saving homework', answers)();
        return api.me.putHomework(
          iid.value,
          sid.value,
          session.value!.homework[0].userHomeworkId!, // shouldbeset onbeforemount
          {
            answers,
            isContentRead: allContentRead.value,
          },
        );
      }
      return intervention.value!;
    },
    (newIntervention) => {
      if (allContentRead.value) {
        // Defensive: if the BE is rolled back to a no-content response, the
        // fetch wrapper returns null. Don't clobber intervention.value — the
        // parent session template dereferences it and would crash.
        if (newIntervention) {
          intervention.value = newIntervention;
        }
        currentIndex.value = 0;
        model.value = [];
        isOpen.value = false;
        // The homework is a task of its own on Today, and this is where it is done (IPD-3328).
        snackbarStore.showToast(t('snackbar_task_completed'));
      }
    },
  )
;

function onValidationError(element: any): void {
  ((element as ComponentPublicInstance).$el as HTMLElement).scrollIntoView({behavior: 'smooth'});
}

</script>
<template>
  <loading-error id="homework-modal" :loading="loading" :error="error" type="component">
    <balm-dialog v-model="isOpen" type="h900">
      <q-form
        class="qform"
        @submit="saveSession"
        @validation-error="onValidationError"
      >
        <fieldset :disabled="isCompleted" class="homework-form">
          <template v-for="(component, i) in components">
            <!-- Should be v-if instead of v-show cause of form-submit -->
            <section-component
              v-if="isCompleted || i <= currentIndex"
              :key="getId(component)"
              :ref="el => sectionRefs.assign(el, i)"
              v-model="model[i]"
              v-model:hide-continue="hideContinue"
              :sid="sid"
              :iid="iid"
              :primary-feelings="intervention!.primaryFeelings"
              :loading="false"
              :thinking-traps="intervention!.thinkingTraps"
              :component="component"
            />
          </template>
          <balm-btn
            v-if="!hideContinue && !isCompleted"
            theme="white"
            paddings="p3-7"
            class="continue-btn"
            :label="isSubmitLabel ? $t('submit') : $t('continue')"
            type="submit"
            :loading="savingInProgress"
          />
        </fieldset>
      </q-form>
    </balm-dialog>
  </loading-error>
</template>
<style scoped lang="scss">
.qform {
  gap: var(--Spacing-spacing-4);
}

.homework-form {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  border: none;
  padding: 0;
  margin: 0;
}

.continue-btn {
  margin-left: auto;
  margin-top: var(--Spacing-spacing-5);
  margin-bottom: var(--Spacing-spacing-1);
}
</style>
